import api from './api';

export interface Employee {
  id: number;
  name: string;
  email: string;
  role: string;
  position: string | null;
  status: 'active' | 'inactive';
  lastLoginIp: string;
  lastLoginDate: string;
  avatar: string;
  is_blocked?: boolean;
  is_system_admin?: boolean;
  /** Дата удаления (только в списке архива) */
  deleted_at?: string | null;
}

export interface UsersListParams {
  page?: number;
  per_page?: number;
  search?: string;
  role?: string;
  position?: string;
  status?: string;
  /** Список только из мягко удалённых (архив) */
  archive?: boolean;
}

export interface UsersListResponse {
  data: Employee[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface UserProfile {
  id: number;
  name: string;
  email: string;
  phone?: string | null;
  birth_date?: string | null;
  telegram_id?: string | null;
  avatar?: string | null;
  position?: string | null;
  practice?: string | null;
  manager?: string | null;
  role?: string;
  is_blocked?: boolean;
  is_system_admin?: boolean;
  /** Заполнено, если пользователь в архиве (мягкое удаление) */
  deleted_at?: string | null;
}

export interface UpdateUserData {
  name?: string;
  email?: string;
  phone?: string | null;
  birth_date?: string | null;
  telegram_id?: string | null;
  avatar?: File | null;
  position?: string | null;
  position_id?: number | null;
  practice?: string | null;
  manager?: string | null;
  note?: string | null;
  is_system_admin?: boolean;
  is_user_manager?: boolean;
  password?: string | null;
  send_login_info?: boolean;
}

export interface CreateUserData {
  name: string;
  email: string;
  phone?: string | null;
  birth_date?: string | null;
  telegram_id?: string | null;
  position?: string | null;
  position_id?: number | null;
  practice?: string | null;
  manager?: string | null;
  note?: string | null;
  is_system_admin?: boolean;
  is_user_manager?: boolean;
  password?: string | null;
  send_login_info?: boolean;
}

export interface DashboardUser {
  id: number;
  name: string;
  email: string;
  role: string;
  avatar: string;
  is_online: boolean;
  practice?: {
    id: number | null;
    name: string;
  } | null;
  team_members?: DashboardUser[];
}

export interface DashboardUsersResponse {
  managing_partners: DashboardUser[];
  partners: DashboardUser[];
  practice_heads: DashboardUser[];
}

export const usersService = {
  /**
   * Получить пользователей для дашборда (сгруппированные по должностям)
   */
  async getDashboardUsers(): Promise<DashboardUsersResponse> {
    const { data } = await api.get<DashboardUsersResponse>('/users/dashboard');
    return data;
  },

  /**
   * Получить список пользователей
   */
  async getUsers(params: UsersListParams = {}): Promise<UsersListResponse> {
    const { data } = await api.get<UsersListResponse>('/users', { params });
    return data;
  },

  /**
   * Получить информацию о пользователе по ID
   */
  async getUserById(id: number): Promise<UserProfile> {
    const { data } = await api.get<UserProfile>(`/users/${id}`);
    return data;
  },

  /**
   * Создать пользователя
   */
  async createUser(userData: CreateUserData): Promise<UserProfile> {
    const { data } = await api.post<{ user: UserProfile }>(`/users`, userData);
    return data.user;
  },

  /**
   * Обновить информацию о пользователе по ID
   */
  async updateUser(id: number, userData: UpdateUserData): Promise<UserProfile> {
    const hasAvatar = userData.avatar && userData.avatar instanceof File;
    const deleteAvatar = userData.avatar === null;
    
    if (hasAvatar || deleteAvatar) {
      const formData = new FormData();
      
      // Добавляем все поля в FormData
      for (const key in userData) {
        if (Object.prototype.hasOwnProperty.call(userData, key)) {
          const value = userData[key as keyof UpdateUserData];
          
          if (key === 'avatar') {
            if (value instanceof File) {
              formData.append('avatar', value);
            } else if (value === null) {
              // Для удаления аватара отправляем пустую строку
              formData.append('avatar', '');
            }
          } else if (value !== undefined) {
            if (value === null) {
              // Для null значений отправляем пустую строку
              formData.append(key, '');
            } else if (typeof value === 'boolean') {
              // Boolean значения отправляем как '1' или '0'
              formData.append(key, value ? '1' : '0');
            } else if (typeof value === 'string' || typeof value === 'number') {
              formData.append(key, String(value));
            }
          }
        }
      }
      
      formData.append('_method', 'PUT');
      
      const { data } = await api.post<{ user: UserProfile }>(`/users/${id}`, formData);
      return data.user;
    } else {
      const { data } = await api.put<{ user: UserProfile }>(`/users/${id}`, userData);
      return data.user;
    }
  },

  /**
   * Заблокировать/разблокировать пользователя
   */
  async toggleBlock(id: number): Promise<{ user: UserProfile; message: string }> {
    try {
      const { data } = await api.post<{ user: UserProfile; message: string } | { error: string }>(`/users/${id}/toggle-block`);
      
      // Если вернулась ошибка
      if ('error' in data) {
        throw new Error(data.error);
      }
      
      return data as { user: UserProfile; message: string };
    } catch (error: any) {
      // Если это ошибка от сервера с сообщением
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  /**
   * Мягкое удаление (в архив)
   */
  async softDeleteUser(id: number): Promise<{ message: string }> {
    const { data } = await api.delete<{ message: string }>(`/users/${id}`);
    return data;
  },

  /**
   * Восстановить из архива
   */
  async restoreUser(id: number): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>(`/users/${id}/restore`);
    return data;
  },
};
